import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Star, Check, Crown, Sparkles } from 'lucide-react';
import { trackPricingClick } from '../../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SpinningPrice({ text, className = '' }) {
  const prevText = useRef(text);
  const [spinning, setSpinning] = useState(false);
  const [display, setDisplay] = useState(text);

  useEffect(() => {
    if (prevText.current === text) return;
    prevText.current = text;
    setSpinning(true);

    // Show random digits cycling for 400ms
    let frame = 0;
    const symbols = text.match(/^[^\d]*/)?.[0] || '';
    const digits = text.replace(/^[^\d]*/, '');
    const interval = setInterval(() => {
      frame++;
      const scrambled = digits.replace(/\d/g, () => Math.floor(Math.random() * 10).toString());
      setDisplay(symbols + scrambled);
    }, 50);

    setTimeout(() => {
      clearInterval(interval);
      setDisplay(text);
      setSpinning(false);
    }, 400);

    return () => clearInterval(interval);
  }, [text]);

  return (
    <span className={`${className} inline-block`}>
      {display.split('').map((char, i) => (
        <span key={i} className="inline-block">{char}</span>
      ))}
    </span>
  );
}

export function PricingSection({ content, theme, t, pricingCurrency, setPricingCurrency, goToSignup, betaMode }) {
  const rates = content.pricing?.currency_rates || {};
  const [pricingCfg, setPricingCfg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing`).then(r => setPricingCfg(r.data)).catch(() => {});
  }, []);

  const activeDiscounts = Object.entries(pricingCfg?.plans || {})
    .filter(([_, p]) => p.is_discounted)
    .map(([id, p]) => ({ id, label: p.discount_label || `Save ${Math.round(p.discount_percent || 0)}%`, percent: Math.round(p.discount_percent || 0) }));

  const currencyConfig = {
    eur: { symbol: '\u20AC', rate: 1 },
    gbp: { symbol: '\u00A3', rate: parseFloat(rates.gbp) || 0.84 },
    usd: { symbol: '$', rate: parseFloat(rates.usd) || 1.09 },
  };
  const cc = currencyConfig[pricingCurrency] || currencyConfig.eur;
  // Soft-launch override: when beta mode is on, swap the Subscribe / Get Started
  // CTA copy to a "Join the Beta" call-to-action so drive-by visitors don't
  // attempt a real card purchase while Stripe is still in test mode.
  const inBeta = !!betaMode?.enabled;
  const betaCtaLabel = 'Join the Beta';

  const convertPrice = (priceStr) => {
    if (!priceStr) return priceStr;
    return priceStr.replace(/[\u20AC\u00A3$]\s?[\d,]+\.?\d*/g, (match) => {
      const num = parseFloat(match.replace(/[^\d.]/g, ''));
      if (isNaN(num)) return match;
      const converted = (num * cc.rate).toFixed(2);
      return `${cc.symbol}${converted}`;
    });
  };

  return (
    <section key="pricing" id="pricing" data-section="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          {content.pricing?.badge && (
            <Badge className="bg-[#007a33] text-white border-[#007a33] mb-4 px-4 py-1 hover:bg-[#007a33] text-[0.7rem] font-black tracking-[1px]">
              {content.pricing.badge}
            </Badge>
          )}
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{content.pricing?.title || 'Simple, Fair Pricing'}</h2>
          <p className={`${t.textSecondary} text-lg`}>{content.pricing?.subtitle || 'Full premium access on every plan. No catches, no hidden costs.'}</p>
          {/* Currency Toggle */}
          <div className="flex items-center justify-center gap-1 mt-6" data-testid="currency-toggle">
            {[
              { key: 'eur', symbol: '\u20AC', label: 'EUR' },
              { key: 'gbp', symbol: '\u00A3', label: 'GBP' },
              { key: 'usd', symbol: '$', label: 'USD' },
            ].map((c) => (
              <button
                key={c.key}
                onClick={() => setPricingCurrency(c.key)}
                className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  pricingCurrency === c.key
                    ? 'bg-[#007a33] text-white'
                    : theme === 'dark'
                    ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                data-testid={`currency-${c.key}-btn`}
              >
                {c.symbol} {c.label}
              </button>
            ))}
          </div>
          {activeDiscounts.length > 0 && (
            <div
              className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full bg-amber-500 text-white text-xs font-bold uppercase tracking-wider shadow-lg"
              data-testid="landing-promo-strip"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {activeDiscounts.length === 1
                ? `${activeDiscounts[0].label} \u2014 limited time on the ${activeDiscounts[0].id} plan`
                : `Promo on ${activeDiscounts.length} plans \u2014 save up to ${Math.max(...activeDiscounts.map(d => d.percent))}%`}
            </div>
          )}
          {inBeta && (
            <div
              className={`inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full border ${
                theme === 'dark'
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                  : 'border-emerald-600/40 bg-emerald-50 text-emerald-700'
              } text-xs font-bold uppercase tracking-wider`}
              data-testid="pricing-beta-strip"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Free during beta · pick a plan to lock in your price when we go live
            </div>
          )}
        </div>
        <div className="max-w-5xl mx-auto">
          {/* Recurring plans — Weekly / Monthly / Annual side-by-side on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" data-testid="pricing-recurring-grid">
            {content.pricing?.plans?.filter(plan => {
              const isLifetime = plan.id === 'lifetime' || plan.name?.toLowerCase().includes('lifetime');
              return !isLifetime;
            }).map((plan, index) => {
              const planDiscount = activeDiscounts.find(d => d.id === plan.id);
              const cfgPlan = pricingCfg?.plans?.[plan.id];
              const fmtEUR = (n) => `\u20AC${(Number(n) || 0).toFixed(2)}`;
              let displayPrice = plan.price;
              let displayStrike = null;
              if (cfgPlan) {
                const eff = Number(cfgPlan.effective_amount ?? cfgPlan.base_amount) || 0;
                const base = Number(cfgPlan.base_amount) || 0;
                if (plan.id === 'annual') {
                  const monthly = Number(cfgPlan.monthly_equivalent);
                  const monthlyBase = monthly || (base / 12);
                  const pct = Number(cfgPlan.discount_percent) || 0;
                  const monthlyEff = cfgPlan.is_discounted
                    ? Math.round(monthlyBase * (1 - pct / 100) * 100) / 100
                    : monthlyBase;
                  displayPrice = fmtEUR(monthlyEff);
                  if (cfgPlan.is_discounted) displayStrike = fmtEUR(monthlyBase);
                } else {
                  displayPrice = fmtEUR(eff);
                  if (cfgPlan.is_discounted) displayStrike = fmtEUR(base);
                }
              }

              const badgeColor = 'bg-[#007a33] text-white';
              const cardBorder = plan.highlighted
                ? (theme === 'dark' ? 'bg-[#007a33]/20 border-[#007a33]' : 'bg-[#007a33]/5 border-[#007a33] shadow-lg')
                : t.card;
              const btnClass = plan.highlighted
                ? 'bg-[#007a33] hover:bg-[#005c26] text-white'
                : (theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-900');
              const checkColor = t.accentText;
              // Mobile-only display order: Annual → Monthly → Weekly.
              // Desktop keeps the natural source order (Weekly | Monthly | Annual side-by-side).
              const mobileOrderClass =
                plan.id === 'annual'  ? 'order-1 md:order-none' :
                plan.id === 'monthly' ? 'order-2 md:order-none' :
                plan.id === 'weekly'  ? 'order-3 md:order-none' :
                '';

              return (
                <Card key={plan.id || index} className={`relative ${cardBorder} ${mobileOrderClass}`}>
                  {planDiscount && (
                    <div className="absolute -top-3 right-3 bg-amber-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-md flex items-center gap-1" data-testid={`landing-plan-${plan.id}-discount`}>
                      <Sparkles className="w-3 h-3" /> {planDiscount.label}
                    </div>
                  )}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className={`${badgeColor} flex items-center gap-1`}>
                        {plan.highlighted && <Star className="w-3 h-3" />}
                        {plan.badge}
                      </Badge>
                    </div>
                  )}
                  <CardContent className="p-6 pt-8 flex flex-col h-full">
                    <h3 className={`text-xl font-bold ${t.text} mb-1`}>{plan.name}</h3>
                    <p className={`${plan.highlighted ? 'text-[#007a33] font-medium' : t.textSecondary} text-sm mb-4`}>
                      {convertPrice(plan.description)}
                    </p>
                    <div className="mb-1 flex items-baseline gap-2">
                      <SpinningPrice text={convertPrice(displayPrice)} className={`text-4xl font-bold ${t.text}`} />
                      <span className={t.textSecondary}>{plan.period}</span>
                      {displayStrike && (
                        <span className={`text-base font-semibold line-through ${t.textMuted}`} data-testid={`landing-plan-${plan.id}-strike`}>
                          {convertPrice(displayStrike)}
                        </span>
                      )}
                    </div>
                    {plan.subPrice && (
                      <p className={`text-xs ${t.textMuted} mb-2`}>{convertPrice(plan.subPrice)}</p>
                    )}
                    {/* Intro pill — auto-driven by /api/pricing's trial fields.
                        Surfaces the active intro (paid or free) on the landing
                        card so visitors know about the entry offer before they
                        click. Renders only when admin has Enable Trial on AND
                        trial_days > 0 AND intro hasn't expired. */}
                    {(() => {
                      if (!cfgPlan || plan.id !== 'annual') return null;
                      if (cfgPlan.trial_enabled === false) return null;
                      if (cfgPlan.intro_active === false) return null;
                      const tDays = Number(cfgPlan.trial_days) || 0;
                      if (tDays <= 0) return null;
                      const tAmt = cfgPlan.trial_amount;
                      const hasPaidIntro = tAmt !== null && tAmt !== undefined && Number(tAmt) > 0;
                      const hasFreeTrial = tAmt !== null && tAmt !== undefined && Number(tAmt) === 0;
                      let label = '';
                      if (hasPaidIntro) {
                        const sym = cc.symbol;
                        const amt = (Number(tAmt) * cc.rate).toFixed(2);
                        const unit = tDays === 7 ? '1 week' : (tDays % 7 === 0 ? `${tDays / 7} weeks` : `${tDays} days`);
                        label = `Try ${unit} for ${sym}${amt}`;
                      } else if (hasFreeTrial) {
                        label = `Free ${tDays}-day trial`;
                      }
                      if (!label) return null;

                      // Compute live countdown when the admin has set an expiry
                      let countdown = '';
                      const expIso = cfgPlan.intro_expires_at;
                      if (expIso) {
                        try {
                          const expMs = new Date(expIso).getTime();
                          const diffMs = expMs - Date.now();
                          if (!isNaN(expMs) && diffMs > 0) {
                            const diffDays = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
                            const diffHours = Math.ceil(diffMs / (60 * 60 * 1000));
                            if (diffDays <= 1) {
                              countdown = `Ends in ${diffHours}h`;
                            } else if (diffDays <= 14) {
                              countdown = `Ends in ${diffDays} days`;
                            } else {
                              const expDate = new Date(expMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                              countdown = `Until ${expDate}`;
                            }
                          }
                        } catch { /* ignore */ }
                      }

                      return (
                        <div className="mb-5 flex flex-wrap items-center gap-1.5">
                          <div
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 text-[11px] font-bold uppercase tracking-wider"
                            data-testid={`landing-plan-${plan.id}-intro-pill`}
                          >
                            <Sparkles className="w-3 h-3" /> {label}
                          </div>
                          {countdown && (
                            <div
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-700 text-[10px] font-bold uppercase tracking-wider animate-pulse"
                              data-testid={`landing-plan-${plan.id}-intro-countdown`}
                            >
                              ⏱ {countdown}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <ul className="space-y-2.5 mb-6 flex-1">
                      {plan.features?.map((f, i) => (
                        <li key={i} className={`flex items-center gap-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Check className={`w-4 h-4 ${checkColor} flex-shrink-0`} />
                          <span dangerouslySetInnerHTML={{ __html: convertPrice(f) }} />
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full ${btnClass}`}
                      onClick={() => { trackPricingClick(plan.name); goToSignup(); }}
                      data-testid={`pricing-${(plan.id || plan.name).toLowerCase().replace(/\s/g, '-')}-btn`}
                    >
                      {inBeta ? betaCtaLabel : (plan.ctaText || 'Get Started')}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Lifetime — full-width card below recurring plans on desktop, stacked on mobile */}
          {content.pricing?.plans?.filter(plan => plan.id === 'lifetime' || plan.name?.toLowerCase().includes('lifetime')).map((plan, index) => {
            const planDiscount = activeDiscounts.find(d => d.id === plan.id);
            const cfgPlan = pricingCfg?.plans?.[plan.id];
            const fmtEUR = (n) => `\u20AC${(Number(n) || 0).toFixed(2)}`;
            let displayPrice = plan.price;
            let displayStrike = null;
            if (cfgPlan) {
              const eff = Number(cfgPlan.effective_amount ?? cfgPlan.base_amount) || 0;
              const base = Number(cfgPlan.base_amount) || 0;
              displayPrice = fmtEUR(eff);
              if (cfgPlan.is_discounted) displayStrike = fmtEUR(base);
            }
            const cardBorder = theme === 'dark' ? 'bg-amber-900/20 border-amber-700/50' : 'bg-amber-50 border-amber-300 shadow-md';
            const btnClass = theme === 'dark' ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white';
            const checkColor = theme === 'dark' ? 'text-amber-400' : 'text-amber-600';

            return (
              <Card key={plan.id || `lifetime-${index}`} className={`relative mt-8 ${cardBorder}`} data-testid="pricing-lifetime-card">
                {planDiscount && (
                  <div className="absolute -top-3 right-3 bg-amber-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-md flex items-center gap-1" data-testid={`landing-plan-${plan.id}-discount`}>
                    <Sparkles className="w-3 h-3" /> {planDiscount.label}
                  </div>
                )}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500 text-white flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 pt-8 md:grid md:grid-cols-5 md:gap-8 md:items-center">
                  {/* Left: title / price / description / CTA — spans 2 cols on desktop */}
                  <div className="md:col-span-2 space-y-3">
                    <h3 className={`text-2xl font-bold ${t.text}`}>{plan.name}</h3>
                    <p className={`${theme === 'dark' ? 'text-amber-400 font-medium' : 'text-amber-700 font-medium'} text-sm`}>
                      {convertPrice(plan.description)}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <SpinningPrice text={convertPrice(displayPrice)} className={`text-4xl font-bold ${t.text}`} />
                      <span className={t.textSecondary}>{plan.period}</span>
                      {displayStrike && (
                        <span className={`text-base font-semibold line-through ${t.textMuted}`} data-testid={`landing-plan-${plan.id}-strike`}>
                          {convertPrice(displayStrike)}
                        </span>
                      )}
                    </div>
                    {plan.subPrice && (
                      <p className={`text-xs ${t.textMuted}`}>{convertPrice(plan.subPrice)}</p>
                    )}
                    <Button
                      className={`w-full md:w-auto md:px-8 ${btnClass} mt-2`}
                      onClick={() => { trackPricingClick(plan.name); goToSignup(); }}
                      data-testid={`pricing-${(plan.id || plan.name).toLowerCase().replace(/\s/g, '-')}-btn`}
                    >
                      {inBeta ? betaCtaLabel : (plan.ctaText || 'Get Lifetime Access')}
                    </Button>
                  </div>
                  {/* Right: features — spans 3 cols on desktop, 2-col grid for readability */}
                  <ul className="mt-6 md:mt-0 md:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {plan.features?.map((f, i) => (
                      <li key={i} className={`flex items-center gap-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        <Check className={`w-4 h-4 ${checkColor} flex-shrink-0`} />
                        <span dangerouslySetInnerHTML={{ __html: convertPrice(f) }} />
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {content.pricing?.footerText && (
          <p className={`text-center text-sm ${t.textMuted} mt-8`}>{content.pricing.footerText}</p>
        )}
      </div>
    </section>
  );
}
